import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { api } from '@/api';

const AuthContext = createContext(null);
const STORAGE_KEY = 'mi_auth';

// Load session synchronously at module scope so axios has Authorization set on the VERY first XHR.
function _readSession() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}
const _initial = _readSession();
if (_initial?.token) {
  api.defaults.headers.common.Authorization = `Bearer ${_initial.token}`;
}

export const TIER_RANK = { guest: 0, starter: 1, pro: 2, fleet: 3 };

export function AuthProvider({ children }) {
  const [session, setSession] = useState(_initial);
  const [entitlement, setEntitlement] = useState(null);

  const refreshEntitlement = useCallback(async (force = false) => {
    if (!api.defaults.headers.common.Authorization) { setEntitlement(null); return null; }
    try {
      const { data } = await api.get('/me/entitlement', { params: force ? { force: true } : {} });
      setEntitlement(data);
      return data;
    } catch { return null; }
  }, []);

  // Only run the /auth/me validator when the token actually changes.
  useEffect(() => {
    if (!session?.token) {
      delete api.defaults.headers.common.Authorization;
      setEntitlement(null);
      return;
    }
    api.defaults.headers.common.Authorization = `Bearer ${session.token}`;
    api.get('/auth/me').then(() => refreshEntitlement()).catch((err) => {
      // Silent logout on expired/invalid token — no toast, no console noise
      if (err?.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        delete api.defaults.headers.common.Authorization;
        setSession(null);
      }
    });
  }, [session?.token, refreshEntitlement]);

  const requestCode = useCallback(async (email, roleHint) => {
    const { data } = await api.post('/auth/request-code', roleHint ? { email, role_hint: roleHint } : { email });
    return data;
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    const { data } = await api.post('/auth/verify-code', { email, code });
    const s = { token: data.token, user: data.user };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSession(s);
    return s;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    delete api.defaults.headers.common.Authorization;
    setSession(null);
    setEntitlement(null);
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user || null,
    token: session?.token || null,
    isAuthed: !!session?.token,
    isAdmin: session?.user?.role === 'admin',
    isPartner: session?.user?.role === 'partner' || session?.user?.role === 'admin',
    entitlement,
    entitledTier: session?.token ? (entitlement?.tier || 'guest') : 'guest',
    refreshEntitlement,
    requestCode, verifyCode, logout,
  }), [session, entitlement, refreshEntitlement, requestCode, verifyCode, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth requires <AuthProvider>');
  return ctx;
}
